/**
 * Reception's ticket preview: what the next slip would say before it is printed,
 * from the live line and the server's estimate. The number is never guessed.
 */
import type { LayoutDocument } from '../layout/types'
import type { QueueEstimate } from './estimate'
import { queueForService } from './plan'
import { queueLetter } from './receptionDesk'
import type { Priority, QueueView, VisitField } from './types'

export interface DraftPreview {
  /** The queue letter the number will carry; '' when no queue takes the service. */
  letter: string
  /** What the server would tell a standard ticket now; null for a priority, which jumps ahead, or before it answered. */
  estimate: QueueEstimate | null
}

/** The draft slip: the letter from the live line, the place and wait from the server. */
export function draftPreview(
  serviceId: string,
  priority: Priority,
  queues: readonly QueueView[],
  estimates: ReadonlyMap<string, QueueEstimate>,
): DraftPreview {
  const queue = queueForService(queues, serviceId)
  if (!queue) return { letter: '', estimate: null }
  const letter = queueLetter(queue)
  if (priority !== 'standard') return { letter, estimate: null }
  return { letter, estimate: estimates.get(queue.queueId) ?? null }
}

/** Whether the slip prints a code: the built-in slip always does, a design only with a QR element. */
export function slipHasCode(design: Pick<LayoutDocument, 'elements'> | null | undefined): boolean {
  return !design || design.elements.some((e) => e.kind === 'qr')
}

/** The fields the one "Visitor details" box can fill: the first text and the first phone field. */
export function visitorFields(offered: readonly VisitField[]): { text?: VisitField; phone?: VisitField } {
  return { text: offered.find((f) => f.type === 'text'), phone: offered.find((f) => f.type === 'phone') }
}

/** Which field a typed value belongs in: a phone field when it reads as a number, else the text field. */
export function visitorTarget(offered: readonly VisitField[], value: string): VisitField | undefined {
  const { text, phone } = visitorFields(offered)
  const v = value.trim()
  const looksLikePhone = /^\+?[\d\s()-]+$/.test(v) && v.replace(/\D/g, '').length >= 5
  if (phone && (looksLikePhone || !text)) return phone
  return text ?? phone
}
